import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { REVIEW_SESSION_LIMIT, type ReviewGrade } from '@shared/srs'
import type { ReviewCard } from '@shared/types/vocabulary'
import { Button } from '@web/components/ui/Button'
import { SpeakButtonById, useAudio } from '@web/lib/audio'
import { useFinishReviewSession, useGradeCard, useReviewQueue } from '@web/lib/review'

/**
 * The flashcard session.
 *
 * Deliberate choices:
 *
 * - **Audio plays itself.** She is learning words she has never heard spoken;
 *   making her tap for that is friction on the one thing she most needs.
 * - **Portuguese first, meaning hidden.** Recall is what strengthens memory;
 *   recognising an English word she already knows strengthens nothing.
 * - **"Again" is not a failure button.** It is graded identically to the rest,
 *   earns the same XP, and simply brings the card back later in the session.
 *   A grading UI that punishes honesty gets dishonest answers.
 * - **A round ends, the session does not.** Twenty cards is a resting point,
 *   not a quota: if more is still due she is offered another round rather than
 *   being sent away. A backlog she cannot dent in a day is a backlog she stops
 *   opening.
 */
export function ReviewSession() {
  const navigate = useNavigate()
  const { data, isPending, error, refetch } = useReviewQueue()
  const gradeCard = useGradeCard()
  const finishSession = useFinishReviewSession()
  const { play } = useAudio()

  const [queue, setQueue] = useState<ReviewCard[] | null>(null)
  const [revealed, setRevealed] = useState(false)
  const [tally, setTally] = useState({ reviewed: 0, again: 0, good: 0, xp: 0 })
  const [done, setDone] = useState(false)
  const [round, setRound] = useState(1)
  const [loadingRound, setLoadingRound] = useState(false)
  const shownAt = useRef(Date.now())

  // Seed the working queue once. Re-seeding on every render of the query data
  // would discard cards she has already answered.
  useEffect(() => {
    if (data && queue === null) setQueue(data.cards)
  }, [data, queue])

  const card = queue?.[0]
  // Counted from live state rather than the original queue length: a card
  // graded "Again" is added back, so a fixed total would show "4/4" twice and
  // then overflow.
  const remaining = queue?.length ?? 0
  const total = tally.reviewed + remaining
  const position = Math.min(tally.reviewed + 1, Math.max(total, 1))

  // Speak each card as it appears. Autoplay is permitted here because the
  // session always begins with a tap, which unlocks audio for the page.
  useEffect(() => {
    if (!card) return
    shownAt.current = Date.now()
    setRevealed(false)
    if (card.audioId) play(card.audioId)
  }, [card, play])

  async function submit(grade: ReviewGrade) {
    if (!card || !queue) return

    const result = await gradeCard.mutateAsync({
      vocabularyId: card.vocabularyId,
      grade,
      durationMs: Date.now() - shownAt.current,
    })

    setTally((t) => ({
      reviewed: t.reviewed + 1,
      again: t.again + (grade === 'again' ? 1 : 0),
      good: t.good + (grade === 'again' ? 0 : 1),
      xp: t.xp + result.xpEarned,
    }))

    // A forgotten word returns near the end of the session rather than
    // immediately — a few cards of separation is what makes the second look a
    // real recall attempt instead of an echo.
    const rest = queue.slice(1)
    const next = result.repeatInSession
      ? [...rest.slice(0, 4), card, ...rest.slice(4)]
      : rest

    setQueue(next)
    if (next.length === 0) {
      await finishSession.mutateAsync()
      setDone(true)
    }
  }

  if (isPending) {
    return (
      <Centered>
        <span
          aria-label="Loading your review"
          className="size-8 animate-spin rounded-full border-3 border-brand-600 border-t-transparent"
        />
      </Centered>
    )
  }

  if (error) {
    return (
      <Centered>
        <p className="text-ink-700">Your review could not be loaded.</p>
        <Button onClick={() => void navigate('/')}>Back</Button>
      </Centered>
    )
  }

  /**
   * Another twenty.
   *
   * The queue is refetched rather than sliced from what is already loaded:
   * every card in the old round has just been rescheduled, and serving them
   * again from a stale list would grade the same word twice.
   */
  async function nextRound() {
    setLoadingRound(true)
    const fresh = await refetch()
    setQueue(fresh.data?.cards ?? [])
    setTally({ reviewed: 0, again: 0, good: 0, xp: 0 })
    setRound((n) => n + 1)
    setDone(false)
    setLoadingRound(false)
  }

  if (done) {
    return (
      <Summary
        tally={tally}
        round={round}
        streak={finishSession.data?.currentStreak ?? 0}
        stillDue={data?.dueCount ?? 0}
        busy={loadingRound}
        onKeepGoing={() => void nextRound()}
        onDone={() => void navigate('/')}
      />
    )
  }

  if (!card) return <NothingDue queue={data} onDone={() => void navigate('/')} />

  return (
    <div className="flex min-h-dvh flex-col bg-sand-50">
      <header className="px-4 pt-[max(0.75rem,var(--safe-top))] pb-3">
        <div className="mx-auto flex max-w-lg items-center gap-3">
          <button
            type="button"
            onClick={() => void navigate('/')}
            aria-label="Leave review"
            className="-ml-2 flex size-10 items-center justify-center rounded-full text-ink-500 hover:bg-sand-100"
          >
            ✕
          </button>
          <div
            className="h-2.5 flex-1 overflow-hidden rounded-full bg-sand-200"
            role="progressbar"
            aria-valuenow={tally.reviewed}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label="Review progress"
          >
            <div
              className="h-full rounded-full bg-brand-600 transition-[width] duration-300"
              style={{ width: `${total === 0 ? 0 : (tally.reviewed / total) * 100}%` }}
            />
          </div>
          <span className="text-sm tabular-nums text-ink-500">
            {position}/{total}
          </span>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col px-4">
        <Card card={card} revealed={revealed} />
      </main>

      <footer className="px-4 pt-3 pb-[max(0.75rem,var(--safe-bottom))]">
        <div className="mx-auto max-w-lg">
          {revealed ? (
            <GradeButtons disabled={gradeCard.isPending} onGrade={(g) => void submit(g)} />
          ) : (
            <Button size="lg" className="w-full" onClick={() => setRevealed(true)}>
              Show meaning
            </Button>
          )}
        </div>
      </footer>
    </div>
  )
}

function Card({ card, revealed }: { card: ReviewCard; revealed: boolean }) {
  return (
    <section className="flex flex-1 flex-col items-center justify-center gap-5 py-6 text-center">
      {card.isNew && (
        <span className="rounded-full bg-gold-200 px-3 py-1 text-xs font-semibold tracking-wide text-gold-700 uppercase">
          New word
        </span>
      )}

      <div className="flex items-center gap-3">
        <h1 className="font-display text-4xl font-bold text-ink-900" lang="pt-BR">
          {card.pt}
        </h1>
        {card.audioId && (
          <SpeakButtonById mediaId={card.audioId} label={card.pt} size="lg" />
        )}
      </div>

      {card.gender && (
        <p className="text-sm text-ink-400">
          {card.gender === 'm' ? 'masculine' : card.gender === 'f' ? 'feminine' : ''}
        </p>
      )}

      {revealed ? (
        <div className="w-full space-y-4">
          <p className="text-2xl text-ink-900">{card.en}</p>

          {card.examplePt && (
            <div className="rounded-card bg-white p-4 text-left ring-1 ring-brand-900/5">
              <div className="flex items-start gap-2">
                <p className="flex-1 text-ink-900" lang="pt-BR">
                  {card.examplePt}
                </p>
                {card.exampleAudioId && (
                  <SpeakButtonById
                    mediaId={card.exampleAudioId}
                    label={card.examplePt as string}
                    size="md"
                  />
                )}
              </div>
              {card.exampleEn && (
                <p className="mt-1 text-sm text-ink-500">{card.exampleEn}</p>
              )}
            </div>
          )}

          {card.regionNote && (
            <p className="rounded-xl border-l-4 border-gold-500 bg-cream px-4 py-3 text-left text-sm text-ink-700">
              {card.regionNote}
            </p>
          )}
        </div>
      ) : (
        <p className="text-ink-400">Do you remember what this means?</p>
      )}
    </section>
  )
}

/**
 * Four buttons, weighted by colour rather than by size.
 *
 * "Again" is clay, not red, and sits at the same visual weight as the rest:
 * the moment it looks like the punishment option, it stops being pressed
 * honestly and the whole schedule degrades.
 */
function GradeButtons({
  disabled,
  onGrade,
}: {
  disabled: boolean
  onGrade: (grade: ReviewGrade) => void
}) {
  const options: Array<{ grade: ReviewGrade; label: string; hint: string; tone: string }> = [
    { grade: 'again', label: 'Again', hint: 'No idea', tone: 'bg-clay-50 text-clay-700 ring-clay-300' },
    { grade: 'hard', label: 'Hard', hint: 'Struggled', tone: 'bg-gold-100 text-gold-700 ring-gold-300' },
    { grade: 'good', label: 'Good', hint: 'Got it', tone: 'bg-brand-100 text-brand-700 ring-brand-300' },
    { grade: 'easy', label: 'Easy', hint: 'Instant', tone: 'bg-brand-600 text-white ring-brand-600' },
  ]

  return (
    <div className="grid grid-cols-4 gap-2">
      {options.map((option) => (
        <button
          key={option.grade}
          type="button"
          disabled={disabled}
          onClick={() => onGrade(option.grade)}
          className={`flex min-h-16 flex-col items-center justify-center rounded-xl px-1 ring-1 transition-opacity disabled:opacity-50 ${option.tone}`}
        >
          <span className="text-sm font-semibold">{option.label}</span>
          <span className="text-[0.68rem] opacity-80">{option.hint}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * The end of a round.
 *
 * When more is still due, continuing is the primary button and leaving is the
 * quiet one — but leaving stays one tap away and is never framed as quitting.
 * The offer is the point: she cleared twenty, and twenty more is a choice she
 * is now in a position to make.
 */
function Summary({
  tally,
  round,
  streak,
  stillDue,
  busy,
  onKeepGoing,
  onDone,
}: {
  tally: { reviewed: number; again: number; good: number; xp: number }
  round: number
  streak: number
  stillDue: number
  busy: boolean
  onKeepGoing: () => void
  onDone: () => void
}) {
  const more = Math.min(stillDue, REVIEW_SESSION_LIMIT)

  return (
    <Centered>
      <p className="text-6xl" aria-hidden="true">
        {tally.again === 0 ? '🎉' : '🌱'}
      </p>
      <h1 className="font-display text-3xl font-bold">
        {round === 1 ? 'Review done' : `Round ${round} done`}
      </h1>
      <p className="text-ink-700">
        {tally.reviewed} {tally.reviewed === 1 ? 'word' : 'words'} reviewed
        {tally.again > 0 && ` · ${tally.again} to see again`}
      </p>
      <p className="text-sm text-ink-500">
        {tally.again === 0
          ? 'Every word came back to you.'
          : 'The ones you missed will come round sooner.'}
      </p>

      <div className="flex gap-3">
        {tally.xp > 0 && (
          <span className="rounded-full bg-brand-100 px-3 py-1 text-sm font-semibold text-brand-700">
            +{tally.xp} XP
          </span>
        )}
        {streak > 0 && (
          <span className="rounded-full bg-gold-200 px-3 py-1 text-sm font-semibold text-gold-700">
            🔥 {streak} day{streak === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {more > 0 ? (
        <div className="flex w-full max-w-xs flex-col gap-2">
          <p className="text-sm text-ink-500">
            {stillDue} more {stillDue === 1 ? 'word is' : 'words are'} waiting.
          </p>
          <Button size="lg" disabled={busy} onClick={onKeepGoing}>
            {busy ? 'Loading…' : `Keep going · ${more} more`}
          </Button>
          <button
            type="button"
            onClick={onDone}
            className="min-h-11 text-sm font-semibold text-ink-500 hover:text-ink-700"
          >
            That’s enough for today
          </button>
        </div>
      ) : (
        <Button size="lg" onClick={onDone}>
          Done
        </Button>
      )}
    </Centered>
  )
}

function NothingDue({
  queue,
  onDone,
}: {
  queue: { dueCount: number; newAvailable: number } | undefined
  onDone: () => void
}) {
  const hasLockedNew = (queue?.newAvailable ?? 0) > 0

  return (
    <Centered>
      <p className="text-6xl" aria-hidden="true">
        ☕
      </p>
      <h1 className="font-display text-3xl font-bold">Nothing due</h1>
      <p className="max-w-xs text-ink-700">
        {hasLockedNew
          ? 'You have met today’s new words already. More arrive tomorrow — spacing them out is what makes them stick.'
          : 'Finish a lesson and its words will show up here for review.'}
      </p>
      <Button size="lg" onClick={onDone}>
        Back
      </Button>
    </Centered>
  )
}


function Centered({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-lg flex-col items-center justify-center gap-4 px-6 text-center">
      {children}
    </main>
  )
}
